import React, { useState, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const galleryImages = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/tatj6r4b_PR-180.jpg',
    alt: 'Signature dishes selection'
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/jjro8jec_PR-168.jpg',
    alt: 'Wine and appetizers'
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/uowa1dpx_PR-165.jpg',
    alt: 'Desserts selection'
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/38dwctmn_PR-193.jpg',
    alt: 'Georgian feast'
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/11i9druz_PR-195.jpg',
    alt: 'Main courses'
  },
  {
    id: 6,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/5iays7lh_PR-63.jpg',
    alt: 'Signature dishes'
  },
  {
    id: 7,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/xpdagmlw_PR-109.jpg',
    alt: 'Main courses selection'
  },
  {
    id: 8,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/eizkeh3d_PR-121.jpg',
    alt: 'Georgian delicacies'
  },
  {
    id: 9,
    src: 'https://customer-assets.emergentagent.com/job_rose-bistro-1/artifacts/3bkyblnj_PR-50.jpg',
    alt: 'Chicken liver parfait'
  }
];

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedIndex, setSelectedIndex] = useState(null);

  const openLightbox = (index) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);

  const goToPrev = useCallback(() => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
  }, []);

  const goToNext = useCallback(() => {
    setSelectedIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goToPrev, goToNext]);

  const selectedImage = selectedIndex !== null ? galleryImages[selectedIndex] : null;

  return (
    <section 
      id="gallery" 
      className="py-24 md:py-32 bg-[#F5F1EC]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#8B2942] uppercase tracking-[0.3em] text-sm">
            {t('gallery.subtitle')}
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#1A1A1D] mt-4">
            {t('gallery.title')}
          </h2>
          <div className="section-divider mt-8" />
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="gallery-grid">
          {galleryImages.map((image, index) => (
            <div
              key={image.id}
              className={`img-zoom cursor-pointer ${
                index === 0 ? 'col-span-2 row-span-2' : ''
              }`}
              onClick={() => openLightbox(index)}
              data-testid={`gallery-item-${image.id}`}
            >
              <div className={`relative overflow-hidden rounded-sm ${index === 0 ? 'h-full min-h-[400px]' : 'aspect-square'}`}>
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-300" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 transition-colors z-10"
            data-testid="lightbox-close"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 md:p-3 bg-black/30 hover:bg-black/50 rounded-full transition-all z-10"
            data-testid="lightbox-prev"
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>

          {/* Image */}
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 md:p-3 bg-black/30 hover:bg-black/50 rounded-full transition-all z-10"
            data-testid="lightbox-next"
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {selectedIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
